import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSettings } from '../lib/usePublicData';
import { pushDataLayerEvent } from './Analytics';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = [
    'Algemene vraag',
    'Pers',
    'Organisatie',
];

const initialState = {
    name: '',
    company: '',
    email: '',
    phone: '',
    subject: 'Algemene vraag',
    message: '',
    hp_field: '',
};

const ContactBlock = ({ title, person, phone, email, accent, testId }) => (
    <div
        data-testid={testId}
        className={`${accent} border-2 border-[var(--green)] rounded-[24px] p-6 sm:p-7 shadow-[0_6px_0_var(--green-dark)]`}
    >
        <span className="font-display text-xs uppercase tracking-wider text-[#006b44]/80">
            {title}
        </span>
        <h3
            className="mt-2 font-display zz-outline-title-dark"
            style={{ fontSize: 'clamp(1.5rem, 3.2vw, 2rem)' }}
        >
            {person}
        </h3>
        <ul className="mt-5 space-y-3">
            <li>
                <a
                    href={`tel:${phone.replace(/\s+/g, '')}`}
                    data-testid={`${testId}-phone`}
                    className="inline-flex items-center gap-2 text-base text-[var(--green-dark)] hover:text-[var(--green)] transition-colors"
                >
                    <span className="w-9 h-9 rounded-full bg-[var(--green)] text-cream flex items-center justify-center">
                        <Phone size={16} />
                    </span>
                    {phone}
                </a>
            </li>
            <li>
                <a
                    href={`mailto:${email}`}
                    data-testid={`${testId}-email`}
                    className="inline-flex items-center gap-2 text-base text-[var(--green-dark)] hover:text-[var(--green)] transition-colors break-all"
                >
                    <span className="w-9 h-9 rounded-full bg-[var(--green)] text-cream flex items-center justify-center flex-shrink-0">
                        <Mail size={16} />
                    </span>
                    {email}
                </a>
            </li>
        </ul>
    </div>
);

export const Contact = () => {
    const { data: settings } = useSettings();
    const [state, setState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState('idle');
    const [errMsg, setErrMsg] = useState('');

    const onChange = (e) => {
        const { name, value } = e.target;
        setState((s) => ({ ...s, [name]: value }));
        if (errors[name]) setErrors((es) => ({ ...es, [name]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = {};
        if (!state.email.trim()) errs.email = 'Vul je e-mail in.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) errs.email = 'Geen geldig e-mailadres.';
        if (!state.message.trim()) errs.message = 'Schrijf een korte boodschap.';
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setStatus('submitting');
        setErrMsg('');
        try {
            await axios.post(`${API_BASE}/contact-messages`, state, {
                headers: { 'Content-Type': 'application/json' },
            });
            setStatus('success');
            pushDataLayerEvent('contact_message_submit', { subject: state.subject });
            setState(initialState);
        } catch (err) {
            setStatus('error');
            const msg = err.response?.data?.detail || 'Er ging iets mis. Probeer het later opnieuw.';
            setErrMsg(typeof msg === 'string' ? msg : 'Er ging iets mis.');
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-16 sm:py-24 bg-vakjes"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="text-center mb-10 sm:mb-12">
                    <h2
                        data-testid="contact-title"
                        className="zz-outline-title font-display"
                        style={{ fontSize: 'clamp(2.25rem, 6vw, 4rem)' }}
                    >
                        CONTACT
                    </h2>
                </div>

                {/* 2 contact blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7 mb-10 sm:mb-12">
                    <ContactBlock
                        title="Partnerships"
                        person={settings?.contact_partnerships_name || 'Jaak Fontaine'}
                        phone={settings?.contact_partnerships_phone || '+32 497 52 20 65'}
                        email={settings?.contact_partnerships_email || 'info@jaakfontaine.be'}
                        accent="bg-cream/95"
                        testId="contact-block-partnerships"
                    />
                    <ContactBlock
                        title="Organisatie"
                        person={settings?.contact_organization_name || 'Brecht Muylaert'}
                        phone={settings?.contact_organization_phone || '+32 483 24 03 72'}
                        email={settings?.contact_organization_email || 'info@brightevents.be'}
                        accent="bg-cream/95"
                        testId="contact-block-organisatie"
                    />
                </div>

                {/* General contact form */}
                {status === 'success' ? (
                    <div
                        data-testid="contact-success"
                        className="bg-[var(--mint-soft)] border-2 border-[var(--green)] rounded-[24px] p-8 text-center shadow-[0_6px_0_var(--green-dark)]"
                    >
                        <CheckCircle2 className="mx-auto text-[var(--green)] mb-3" size={44} />
                        <h3 className="font-display zz-outline-title-dark text-xl sm:text-2xl">
                            Bedankt voor je bericht!
                        </h3>
                        <p className="mt-3 text-[var(--green-dark)]">
                            We nemen zo snel mogelijk contact met je op.
                        </p>
                        <button
                            type="button"
                            onClick={() => setStatus('idle')}
                            data-testid="contact-success-reset"
                            className="zz-btn zz-btn--primary mt-6 !text-xs"
                        >
                            Nieuw bericht
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        noValidate
                        data-testid="contact-form"
                        className="bg-cream border-2 border-[var(--green)] rounded-[24px] p-6 sm:p-10 shadow-[0_6px_0_var(--green-dark)]"
                    >
                        <h3
                            className="font-display zz-outline-title-dark text-center"
                            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 2rem)' }}
                        >
                            STUUR ONS EEN BERICHT
                        </h3>

                        {/* Honeypot */}
                        <div style={{ display: 'none' }} aria-hidden="true">
                            <label>
                                Niet invullen
                                <input
                                    type="text"
                                    name="hp_field"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={state.hp_field}
                                    onChange={onChange}
                                />
                            </label>
                        </div>

                        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Naam" name="name" value={state.name} onChange={onChange} testId="contact-input-name" autoComplete="name" />
                            <Field label="Bedrijf" name="company" value={state.company} onChange={onChange} testId="contact-input-company" autoComplete="organization" />
                            <Field label="E-mail *" name="email" type="email" value={state.email} onChange={onChange} error={errors.email} testId="contact-input-email" autoComplete="email" />
                            <Field label="Telefoon" name="phone" type="tel" value={state.phone} onChange={onChange} testId="contact-input-phone" autoComplete="tel" />

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                                    Onderwerp
                                </label>
                                <select
                                    name="subject"
                                    value={state.subject}
                                    onChange={onChange}
                                    data-testid="contact-input-subject"
                                    className="w-full bg-cream border-2 border-[var(--green)] rounded-full px-4 py-3 text-base text-[var(--green-dark)] font-medium focus:outline-none focus:ring-2 focus:ring-[var(--green)]"
                                >
                                    {SUBJECTS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                                    Bericht *
                                </label>
                                <textarea
                                    name="message"
                                    value={state.message}
                                    onChange={onChange}
                                    rows={5}
                                    data-testid="contact-input-message"
                                    className={`w-full bg-cream border-2 ${
                                        errors.message ? 'border-red-400' : 'border-[var(--green)]'
                                    } rounded-2xl px-4 py-3 text-base text-[var(--green-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--green)] resize-none`}
                                />
                                {errors.message && (
                                    <p className="mt-1.5 text-xs text-red-600 font-semibold">{errors.message}</p>
                                )}
                            </div>
                        </div>

                        {status === 'error' && (
                            <div
                                role="alert"
                                data-testid="contact-error"
                                className="mt-6 bg-red-50 border-2 border-red-300 text-red-800 rounded-2xl p-4 flex items-start gap-3"
                            >
                                <AlertCircle className="flex-shrink-0 mt-0.5" size={18} />
                                <p className="text-sm">{errMsg}</p>
                            </div>
                        )}

                        <div className="mt-8 flex justify-end">
                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                data-testid="contact-submit"
                                className="zz-btn zz-btn--primary w-full sm:w-auto disabled:opacity-50"
                            >
                                {status === 'submitting' ? 'Versturen…' : (<>Verzend bericht <Send size={16} /></>)}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </section>
    );
};

const Field = ({ label, name, type = 'text', value, onChange, error, autoComplete, testId }) => {
    const inputMode = type === 'email' ? 'email' : type === 'tel' ? 'tel' : undefined;
    return (
        <div>
            <label className="block text-xs font-display uppercase tracking-wider text-[var(--green-dark)] mb-2">
                {label}
            </label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                autoComplete={autoComplete}
                inputMode={inputMode}
                enterKeyHint="next"
                data-testid={testId}
                className={`w-full min-h-[44px] bg-cream border-2 ${
                    error ? 'border-red-400' : 'border-[var(--green)]'
                } rounded-full px-4 py-3 text-base text-[var(--green-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--green)]`}
                aria-invalid={!!error}
            />
            {error && <p className="mt-1.5 text-xs text-red-600 font-semibold">{error}</p>}
        </div>
    );
};

export default Contact;
